import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../ui/button';
import { ScrollArea } from '../../ui/scroll-area';
import { ShoppingCart, Trash2, Minus, Plus, StickyNote, X, Banknote, Send, Pencil, RotateCcw } from 'lucide-react';

const PriceEditor = ({ item, itemKey, onChange, onClose }) => {
  const [value, setValue] = useState(Number(item.price).toFixed(2));
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const commit = () => {
    const parsed = Number(String(value).replace(',', '.'));
    if (!Number.isNaN(parsed) && parsed >= 0) {
      onChange(itemKey, parsed);
    }
    onClose();
  };

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') onClose();
        }}
        className="w-20 h-8 px-2 text-right font-bold border-2 border-green-500 rounded-lg outline-none focus:border-green-600 bg-white text-green-700"
        data-testid={`price-edit-input-${itemKey}`}
      />
      <span className="text-sm font-bold text-green-700">€</span>
    </div>
  );
};

const CartPanel = ({ cart, selectedMesa, getCartTotal, getCartCount, updateQuantity, updateItemPrice, clearCart, onNoteClick, onCobrar, onSendToTable, sending }) => {
  const [editingPriceKey, setEditingPriceKey] = useState(null);

  const handlePriceChange = (itemKey, newPrice) => {
    if (typeof updateItemPrice === 'function') {
      updateItemPrice(itemKey, newPrice);
    }
  };

  const resetPrice = (itemKey, originalPrice) => {
    if (typeof updateItemPrice === 'function') {
      updateItemPrice(itemKey, originalPrice);
    }
  };

  return (
    <div className="w-80 bg-white border-l flex flex-col">
      {/* Cart header */}
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
          <ShoppingCart size={20} />
          Pedido
          {getCartCount() > 0 && (
            <span className="bg-green-500 text-white text-sm w-6 h-6 rounded-full flex items-center justify-center">{getCartCount()}</span>
          )}
        </h3>
        {cart.length > 0 && (
          <button onClick={clearCart} className="text-red-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition-colors" data-testid="clear-cart-btn">
            <Trash2 size={18} />
          </button>
        )}
      </div>

      {/* Cart items */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2">
          {cart.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <ShoppingCart size={36} className="mx-auto mb-2 opacity-40" />
              <p>Añade productos</p>
            </div>
          ) : (
            cart.map((item, idx) => {
              const itemKey = item.cartId || item.id;
              const isEditing = editingPriceKey === itemKey;
              const isOverridden = item.price_overridden && item.original_price != null && Number(item.original_price) !== Number(item.price);
              const lineTotal = (item.price * item.quantity).toFixed(2);
              return (
                <div key={item.cartId || `${item.id}-${idx}`} className="bg-gray-50 rounded-xl p-3.5">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="font-semibold text-gray-800 text-sm leading-tight flex-1">{item.name}</p>
                    <button onClick={() => updateQuantity(itemKey, -999)} className="text-gray-300 hover:text-red-500 p-1 transition-colors flex-shrink-0">
                      <X size={16} />
                    </button>
                  </div>
                  {item.optionsText && <p className="text-xs text-gray-500 mb-1">{item.optionsText}</p>}
                  {item.notes && <p className="text-xs text-amber-600 mb-1">Nota: {item.notes}</p>}

                  {/* Price line: unit price + override indicator */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <span>PVP:</span>
                      {isEditing ? (
                        <PriceEditor
                          item={item}
                          itemKey={itemKey}
                          onChange={handlePriceChange}
                          onClose={() => setEditingPriceKey(null)}
                        />
                      ) : (
                        <button
                          onClick={() => setEditingPriceKey(itemKey)}
                          className={`group inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border ${isOverridden ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-gray-200 bg-white text-gray-700 hover:border-green-400 hover:text-green-700'} transition-colors`}
                          data-testid={`price-edit-btn-${itemKey}`}
                          title="Editar precio unitario"
                        >
                          <span className="font-semibold">{Number(item.price).toFixed(2)}€</span>
                          <Pencil size={11} className="opacity-60 group-hover:opacity-100" />
                        </button>
                      )}
                      {isOverridden && !isEditing && (
                        <button
                          onClick={() => resetPrice(itemKey, item.original_price)}
                          className="inline-flex items-center gap-0.5 text-[10px] text-gray-400 hover:text-gray-600 line-through"
                          title={`Restaurar precio original (${Number(item.original_price).toFixed(2)}€)`}
                          data-testid={`price-reset-btn-${itemKey}`}
                        >
                          <RotateCcw size={10} />
                          {Number(item.original_price).toFixed(2)}€
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-1">
                    <div className="flex items-center gap-1">
                      <button onClick={() => updateQuantity(itemKey, -1)} className="w-8 h-8 rounded-lg bg-white border flex items-center justify-center active:scale-95 hover:bg-gray-100">
                        <Minus size={14} />
                      </button>
                      <span className="w-8 text-center font-bold">{item.quantity}</span>
                      <button onClick={() => updateQuantity(itemKey, 1)} className="w-8 h-8 rounded-lg bg-white border flex items-center justify-center active:scale-95 hover:bg-gray-100">
                        <Plus size={14} />
                      </button>
                      <button onClick={() => onNoteClick(item)} className="w-8 h-8 rounded-lg bg-white border flex items-center justify-center active:scale-95 hover:bg-amber-50 ml-1" title="Añadir nota"
                        data-testid={`note-btn-${itemKey}`}>
                        <StickyNote size={14} className="text-amber-500" />
                      </button>
                    </div>
                    <span className={`font-bold ${isOverridden ? 'text-amber-600' : 'text-green-600'}`} data-testid={`line-total-${itemKey}`}>{lineTotal}€</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>

      {/* Cart footer */}
      <div className="p-4 pb-20 border-t bg-gray-50 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-gray-500 text-lg">Total</span>
          <span className="text-3xl font-bold text-green-600" data-testid="cart-total">{getCartTotal().toFixed(2)}€</span>
        </div>
        {selectedMesa && onSendToTable && (
          <Button onClick={() => onSendToTable(selectedMesa)} disabled={cart.length === 0 || sending}
            className="w-full bg-blue-600 hover:bg-blue-700 h-14 text-lg font-bold rounded-xl disabled:opacity-40"
            data-testid="desktop-send-table-btn">
            <Send size={20} className="mr-2" />
            Enviar a mesa {selectedMesa}
          </Button>
        )}
        <Button onClick={onCobrar} disabled={cart.length === 0 || sending}
          className="w-full bg-green-600 hover:bg-green-700 h-14 text-lg font-bold rounded-xl disabled:opacity-40"
          data-testid="desktop-cobrar-btn">
          <Banknote size={22} className="mr-2" />
          Cobrar {getCartTotal() > 0 ? `${getCartTotal().toFixed(2)}€` : ''}
        </Button>
        {selectedMesa
          ? <p className="text-center text-xs text-gray-400">Mesa {selectedMesa} · "Enviar" lo manda a cocina/barra · "Cobrar" cierra la venta</p>
          : <p className="text-center text-xs text-gray-400">Sin mesa · venta rápida sin enviar a cocina</p>}
      </div>
    </div>
  );
};

export default CartPanel;
